// Tax Identification Number (TIN) validation rules by country
// Source: OECD Automatic Exchange Portal and national tax authority guidelines

interface TINRule {
  pattern: RegExp;
  description: string;
  example: string;
}

// Country-specific TIN validation rules
const tinRules: Record<string, TINRule> = {
  // United States - SSN (9 digits) or EIN (9 digits with hyphen)
  US: {
    pattern: /^(\d{3}-?\d{2}-?\d{4}|\d{2}-?\d{7})$/,
    description: 'SSN (XXX-XX-XXXX) or EIN (XX-XXXXXXX)',
    example: '123-45-6789',
  },
  // United Kingdom - UTR (10 digits) or NINO
  GB: {
    pattern: /^(\d{10}|[A-Z]{2}\d{6}[A-D])$/i,
    description: 'UTR (10 digits) or National Insurance Number',
    example: '1234567890',
  },
  // Germany - 11 digits
  DE: {
    pattern: /^\d{11}$/,
    description: '11 digits',
    example: '12345678901',
  },
  // France - 13 digits
  FR: {
    pattern: /^\d{13}$/,
    description: '13 digits',
    example: '1234567890123',
  },
  // Italy - Codice Fiscale (16 alphanumeric)
  IT: {
    pattern: /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i,
    description: 'Codice Fiscale (16 characters)',
    example: 'RSSMRA85M01H501Z',
  },
  // Spain - NIF/NIE (8 digits + letter or letter + 7 digits + letter)
  ES: {
    pattern: /^([0-9]{8}[A-Z]|[XYZ][0-9]{7}[A-Z])$/i,
    description: 'NIF or NIE',
    example: '12345678Z',
  },
  // Netherlands - BSN (9 digits)
  NL: {
    pattern: /^\d{9}$/,
    description: '9 digits (BSN)',
    example: '123456789',
  },
  // Belgium - 11 digits (National Number)
  BE: {
    pattern: /^\d{11}$/,
    description: '11 digits',
    example: '12345678901',
  },
  // Austria - 9 digits
  AT: {
    pattern: /^\d{9}$/,
    description: '9 digits',
    example: '123456789',
  },
  // Greece - AFM (9 digits)
  GR: {
    pattern: /^\d{9}$/,
    description: '9 digits (AFM)',
    example: '123456789',
  },
  // Czech Republic - 9-10 digits
  CZ: {
    pattern: /^\d{9,10}$/,
    description: '9-10 digits',
    example: '1234567890',
  },
  // Hungary - 10 digits
  HU: {
    pattern: /^\d{10}$/,
    description: '10 digits',
    example: '1234567890',
  },
  // Romania - CNP (13 digits) or CIF (2-10 digits)
  RO: {
    pattern: /^(\d{13}|\d{2,10})$/,
    description: 'CNP (13 digits) or CIF (2-10 digits)',
    example: '1234567890123',
  },
  // Bulgaria - EGN (10 digits) or PNF (10 digits)
  BG: {
    pattern: /^\d{10}$/,
    description: '10 digits (EGN/PNF)',
    example: '1234567890',
  },
  // Croatia - OIB (11 digits)
  HR: {
    pattern: /^\d{11}$/,
    description: '11 digits (OIB)',
    example: '12345678901',
  },
  // Slovakia - 9-10 digits
  SK: {
    pattern: /^\d{9,10}$/,
    description: '9-10 digits',
    example: '1234567890',
  },
  // Slovenia - 8 digits
  SI: {
    pattern: /^\d{8}$/,
    description: '8 digits',
    example: '12345678',
  },
  // Estonia - 11 digits (Isikukood)
  EE: {
    pattern: /^\d{11}$/,
    description: '11 digits (Isikukood)',
    example: '38501010123',
  },
  // Latvia - 11 digits (Personas kods)
  LV: {
    pattern: /^\d{6}-?\d{5}$/,
    description: '11 digits (XXXXXX-XXXXX)',
    example: '010185-12345',
  },
  // Lithuania - 11 digits (Asmens kodas)
  LT: {
    pattern: /^\d{11}$/,
    description: '11 digits (Asmens kodas)',
    example: '38501010123',
  },
  // Cyprus - 9 characters (letter + 8 digits or 8 digits + letter)
  CY: {
    pattern: /^([0-9]{8}[A-Z]|[A-Z][0-9]{8})$/i,
    description: '8 digits + letter or letter + 8 digits',
    example: '12345678A',
  },
  // Malta - 7 digits + letter
  MT: {
    pattern: /^\d{7}[GAWMLPHBZ]$/i,
    description: '7 digits + letter (ID card number)',
    example: '1234567M',
  },
  // Luxembourg - 13 digits
  LU: {
    pattern: /^\d{13}$/,
    description: '13 digits',
    example: '1985010112345',
  },
  // Iceland - Kennitala (10 digits)
  IS: {
    pattern: /^\d{6}-?\d{4}$/,
    description: '10 digits (Kennitala)',
    example: '010185-1234',
  },
  // Liechtenstein - 12 digits
  LI: {
    pattern: /^\d{12}$/,
    description: '12 digits',
    example: '123456789012',
  },
  // Switzerland - 13 digits (AHV/AVS)
  CH: {
    pattern: /^756\.\d{4}\.\d{4}\.\d{2}$|^\d{13}$/,
    description: '13 digits or 756.XXXX.XXXX.XX',
    example: '756.1234.5678.97',
  },
  // Ireland - PPS (7 digits + 1-2 letters)
  IE: {
    pattern: /^\d{7}[A-Z]{1,2}$/i,
    description: 'PPS Number (7 digits + 1-2 letters)',
    example: '1234567TW',
  },
  // Portugal - NIF (9 digits)
  PT: {
    pattern: /^\d{9}$/,
    description: '9 digits (NIF)',
    example: '123456789',
  },
  // Poland - PESEL (11 digits) or NIP (10 digits)
  PL: {
    pattern: /^(\d{11}|\d{10})$/,
    description: 'PESEL (11 digits) or NIP (10 digits)',
    example: '12345678901',
  },
  // Sweden - Personnummer (12 digits or 10+hyphen+4)
  SE: {
    pattern: /^(\d{12}|\d{6}-\d{4}|\d{8}-\d{4})$/,
    description: 'Personal Identity Number',
    example: '19850101-1234',
  },
  // Norway - 11 digits
  NO: {
    pattern: /^\d{11}$/,
    description: '11 digits',
    example: '12345678901',
  },
  // Denmark - CPR (10 digits)
  DK: {
    pattern: /^\d{10}$/,
    description: '10 digits (CPR)',
    example: '1234567890',
  },
  // Finland - HETU (11 characters)
  FI: {
    pattern: /^\d{6}[-+A]\d{3}[A-Z0-9]$/i,
    description: 'Personal Identity Code',
    example: '010185-123A',
  },
  // Australia - TFN (9 digits)
  AU: {
    pattern: /^\d{9}$/,
    description: '9 digits (TFN)',
    example: '123456789',
  },
  // New Zealand - IRD (8-9 digits)
  NZ: {
    pattern: /^\d{8,9}$/,
    description: '8-9 digits (IRD)',
    example: '12345678',
  },
  // Canada - SIN (9 digits)
  CA: {
    pattern: /^\d{3}-?\d{3}-?\d{3}$/,
    description: 'SIN (9 digits)',
    example: '123-456-789',
  },
  // Japan - My Number (12 digits)
  JP: {
    pattern: /^\d{12}$/,
    description: '12 digits (My Number)',
    example: '123456789012',
  },
  // South Korea - RRN (13 digits)
  KR: {
    pattern: /^\d{6}-?\d{7}$/,
    description: 'Resident Registration Number',
    example: '850101-1234567',
  },
  // Singapore - NRIC/FIN
  SG: {
    pattern: /^[STFG]\d{7}[A-Z]$/i,
    description: 'NRIC or FIN',
    example: 'S1234567D',
  },
  // Hong Kong - HKID
  HK: {
    pattern: /^[A-Z]{1,2}\d{6}\([0-9A]\)$/i,
    description: 'HKID',
    example: 'A123456(7)',
  },
  // Taiwan - 10 characters (letter + 9 digits)
  TW: {
    pattern: /^[A-Z][12]\d{8}$/i,
    description: 'National ID (letter + 9 digits)',
    example: 'A123456789',
  },
  // Thailand - 13 digits
  TH: {
    pattern: /^\d{13}$/,
    description: '13 digits (National ID)',
    example: '1234567890123',
  },
  // Malaysia - 12 digits (NRIC)
  MY: {
    pattern: /^\d{6}-?\d{2}-?\d{4}$/,
    description: '12 digits (XXXXXX-XX-XXXX)',
    example: '850101-01-1234',
  },
  // Vietnam - 10-13 digits
  VN: {
    pattern: /^\d{10,13}$/,
    description: '10-13 digits',
    example: '1234567890',
  },
  // Philippines - 12 digits (TIN)
  PH: {
    pattern: /^\d{3}-?\d{3}-?\d{3}-?\d{3}$/,
    description: '12 digits (XXX-XXX-XXX-XXX)',
    example: '123-456-789-000',
  },
  // Indonesia - 15-16 digits (NPWP)
  ID: {
    pattern: /^\d{2}\.?\d{3}\.?\d{3}\.?\d{1}-?\d{3}\.?\d{3}$/,
    description: '15 digits (NPWP)',
    example: '12.345.678.9-012.345',
  },
  // Pakistan - 13 digits (CNIC)
  PK: {
    pattern: /^\d{5}-?\d{7}-?\d{1}$/,
    description: '13 digits (XXXXX-XXXXXXX-X)',
    example: '12345-1234567-1',
  },
  // Bangladesh - 10-17 digits (NID/TIN)
  BD: {
    pattern: /^\d{10,17}$/,
    description: '10-17 digits (NID or TIN)',
    example: '1234567890',
  },
  // Sri Lanka - 9-12 characters (NIC)
  LK: {
    pattern: /^(\d{9}[VX]|\d{12})$/i,
    description: 'NIC (9 digits + V/X or 12 digits)',
    example: '123456789V',
  },
  // India - PAN (10 alphanumeric)
  IN: {
    pattern: /^[A-Z]{5}\d{4}[A-Z]$/i,
    description: 'PAN (10 characters)',
    example: 'ABCDE1234F',
  },
  // UAE - TRN (15 digits)
  AE: {
    pattern: /^\d{15}$/,
    description: '15 digits (TRN)',
    example: '100000000000003',
  },
  // Saudi Arabia - 10 digits (TIN) or 15 digits (VAT)
  SA: {
    pattern: /^(\d{10}|\d{15})$/,
    description: '10 digits (TIN) or 15 digits (VAT)',
    example: '1234567890',
  },
  // Qatar - 10 digits (QID) or 11 digits (TIN)
  QA: {
    pattern: /^\d{10,11}$/,
    description: '10-11 digits',
    example: '12345678901',
  },
  // Kuwait - 12 digits (Civil ID)
  KW: {
    pattern: /^\d{12}$/,
    description: '12 digits (Civil ID)',
    example: '123456789012',
  },
  // Bahrain - 9 digits (CPR)
  BH: {
    pattern: /^\d{9}$/,
    description: '9 digits (CPR)',
    example: '123456789',
  },
  // Oman - 8-9 digits
  OM: {
    pattern: /^\d{8,9}$/,
    description: '8-9 digits',
    example: '12345678',
  },
  // Jordan - 10 digits (National ID)
  JO: {
    pattern: /^\d{10}$/,
    description: '10 digits (National ID)',
    example: '1234567890',
  },
  // Lebanon - 7-8 digits
  LB: {
    pattern: /^\d{7,8}$/,
    description: '7-8 digits',
    example: '1234567',
  },
  // Israel - 9 digits (Teudat Zehut)
  IL: {
    pattern: /^\d{9}$/,
    description: '9 digits (Teudat Zehut)',
    example: '123456789',
  },
  // Egypt - 14 digits (National ID)
  EG: {
    pattern: /^\d{14}$/,
    description: '14 digits (National ID)',
    example: '12345678901234',
  },
  // Morocco - 8-9 characters (CIN)
  MA: {
    pattern: /^[A-Z]{1,2}\d{5,7}$/i,
    description: 'CIN (1-2 letters + 5-7 digits)',
    example: 'AB123456',
  },
  // Tunisia - 8 digits
  TN: {
    pattern: /^\d{8}$/,
    description: '8 digits',
    example: '12345678',
  },
  // South Africa - 13 digits
  ZA: {
    pattern: /^\d{13}$/,
    description: '13 digits',
    example: '8501015009087',
  },
  // Brazil - CPF (11 digits) or CNPJ (14 digits)
  BR: {
    pattern: /^(\d{11}|\d{14}|\d{3}\.\d{3}\.\d{3}-\d{2})$/,
    description: 'CPF or CNPJ',
    example: '123.456.789-00',
  },
  // Mexico - RFC (12-13 alphanumeric)
  MX: {
    pattern: /^[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3}$/i,
    description: 'RFC (12-13 characters)',
    example: 'XAXX010101000',
  },
  // Argentina - CUIT/CUIL (11 digits)
  AR: {
    pattern: /^\d{2}-?\d{8}-?\d{1}$/,
    description: '11 digits (XX-XXXXXXXX-X)',
    example: '20-12345678-9',
  },
  // Chile - RUT (8-9 digits + check digit)
  CL: {
    pattern: /^\d{7,8}-?[0-9K]$/i,
    description: 'RUT (7-8 digits + check digit)',
    example: '12345678-9',
  },
  // Colombia - NIT (9-10 digits)
  CO: {
    pattern: /^\d{9,10}$/,
    description: '9-10 digits (NIT)',
    example: '1234567890',
  },
  // Peru - RUC (11 digits)
  PE: {
    pattern: /^\d{11}$/,
    description: '11 digits (RUC)',
    example: '12345678901',
  },
  // Venezuela - RIF (letter + 8-9 digits)
  VE: {
    pattern: /^[VEJPG]-?\d{8,9}$/i,
    description: 'RIF (V/E/J/P/G + 8-9 digits)',
    example: 'V-12345678',
  },
  // Ecuador - RUC (13 digits)
  EC: {
    pattern: /^\d{13}$/,
    description: '13 digits (RUC)',
    example: '1234567890001',
  },
  // Uruguay - RUT (12 digits)
  UY: {
    pattern: /^\d{12}$/,
    description: '12 digits (RUT)',
    example: '123456789012',
  },
  // Paraguay - RUC (variable length)
  PY: {
    pattern: /^\d{6,9}-?\d{1}$/,
    description: 'RUC (6-9 digits + check digit)',
    example: '12345678-9',
  },
  // Bolivia - NIT (variable length)
  BO: {
    pattern: /^\d{7,10}$/,
    description: '7-10 digits (NIT)',
    example: '1234567',
  },
  // Costa Rica - 9-12 digits (Cédula/NITE)
  CR: {
    pattern: /^\d{9,12}$/,
    description: '9-12 digits',
    example: '123456789',
  },
  // Panama - RUC (various formats)
  PA: {
    pattern: /^(\d{1,2}-?\d{1,4}-?\d{1,6}|\d{8,10})$/,
    description: 'RUC or Cédula',
    example: '8-123-456',
  },
};

// Default rule for countries without specific validation
const defaultRule: TINRule = {
  pattern: /^[A-Z0-9\-\.\/\s]{4,25}$/i,
  description: '4-25 alphanumeric characters',
  example: 'ABC123456',
};

export interface TINValidationResult {
  isValid: boolean;
  error?: string;
  hint?: string;
}

export function validateTIN(countryCode: string, tin: string): TINValidationResult {
  // Empty TIN is handled separately
  if (!tin || tin.trim() === '') {
    return { isValid: false, error: 'TIN is required' };
  }

  const trimmedTIN = tin.trim();
  const rule = tinRules[countryCode] || defaultRule;

  if (!rule.pattern.test(trimmedTIN)) {
    return {
      isValid: false,
      error: `Invalid format`,
      hint: `Expected: ${rule.description} (e.g., ${rule.example})`,
    };
  }

  return { isValid: true };
}

export function getTINPlaceholder(countryCode: string): string {
  const rule = tinRules[countryCode];
  if (rule) {
    return `e.g., ${rule.example}`;
  }
  return 'Enter your TIN';
}

export function getTINHint(countryCode: string): string | undefined {
  const rule = tinRules[countryCode];
  return rule?.description;
}
